import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
}

interface AccordionProps {
  items: {
    id: string;
    title: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    defaultOpen?: boolean;
  }[];
  allowMultiple?: boolean;
}
const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  children,
  icon,
  isExpanded = false,
  onToggle,
}) => (
  <div className="border-b border-gray-200 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      aria-expanded={isExpanded}
    >
      <div className="flex items-center gap-3">
        {icon && <span className="text-gray-600">{icon}</span>}
        <span className="text-gray-800 font-medium text-[12px]">{title}</span>
      </div>
      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </button>

    {isExpanded && (
      <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
        {children}
      </div>
    )}
  </div>
);

export const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultiple = false,
}) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    () =>
      items.reduce((acc, item) => {
        if (item.defaultOpen) acc[item.id] = true;
        return acc;
      }, {} as Record<string, boolean>)
  );

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      if (allowMultiple) {
        return { ...prev, [id]: !prev[id] };
      } else {
        // Close all others when opening one
        return { [id]: !prev[id] };
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {items.map((item) => (
        <AccordionItem
          key={item.id}
          title={item.title}
          icon={item.icon}
          isExpanded={expandedItems[item.id]}
          onToggle={() => toggleItem(item.id)}
        >
          {item.content}
        </AccordionItem>
      ))}
    </div>
  );
};

// Optional: Standalone Toggle Switch
interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const Toggle: React.FC<ToggleProps> = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      className="sr-only peer"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors">
      <div
        className={`absolute top-0.5 left-[2px] bg-white rounded-full h-5 w-5 transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      ></div>
    </div>
  </label>
);
